import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getIsOnline } from '@/contexts/ConnectionContext';

const AuthContext = createContext(null);

// Always use relative /api path — works in both preview and production
// The platform ingress routes /api to the backend regardless of domain
const API = '/api';

// Create a single axios instance with credentials enabled for httpOnly cookies
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Offline write-guard — refuses POST/PUT/PATCH/DELETE while the browser is
// offline so any Save/Submit/Update/Create that slips past the Button gate
// fails fast with a clear error instead of silently timing out. GETs are
// allowed through; browsers will surface their own network error if needed.
api.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (!getIsOnline() && method !== 'get' && method !== 'head' && method !== 'options') {
    const err = new Error('You are offline. Reconnect to save.');
    err.code = 'OFFLINE';
    err.isAxiosOffline = true;
    return Promise.reject(err);
  }
  return config;
});

// Flag to prevent concurrent refresh attempts
let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed() {
  refreshSubscribers.forEach(cb => cb());
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb) {
  refreshSubscribers.push(cb);
}

// Response interceptor: auto-refresh on 401, handle 402
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip refresh for auth endpoints themselves to avoid loops
    const isAuthEndpoint = originalRequest.url?.includes('/auth/login')
      || originalRequest.url?.includes('/auth/refresh')
      || originalRequest.url?.includes('/auth/logout');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // Queue this request until refresh completes
        return new Promise((resolve) => {
          addRefreshSubscriber(() => {
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await api.post('/auth/refresh');
        isRefreshing = false;
        onRefreshed();
        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        refreshSubscribers = [];
        // Refresh failed — force logout (handled by AuthProvider via event)
        window.dispatchEvent(new Event('auth:logout'));
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentFacility, setCurrentFacility] = useState(null);

  // Billing state
  const [billingStatus, setBillingStatus] = useState(null);
  const [isBillingLocked, setIsBillingLocked] = useState(false);
  const billingLockedRef = useRef(false);

  // Listen for forced logout from interceptor
  useEffect(() => {
    const handleForcedLogout = () => {
      setUser(null);
      setCurrentFacility(null);
      setBillingStatus(null);
      setIsBillingLocked(false);
      billingLockedRef.current = false;
    };
    window.addEventListener('auth:logout', handleForcedLogout);
    return () => window.removeEventListener('auth:logout', handleForcedLogout);
  }, []);

  // Handle 402 billing locked inside a secondary interceptor scoped to provider state
  useEffect(() => {
    const id = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 402) {
          const detail = error.response?.data?.detail;
          if (detail?.code === 'billing_locked') {
            setIsBillingLocked(true);
            billingLockedRef.current = true;
            setBillingStatus(prev => ({
              ...prev,
              is_locked_for_billing: true,
              subscription_status: detail.subscription_status || prev?.subscription_status,
              trial_end_at: detail.trial_end_at || prev?.trial_end_at,
            }));
          }
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, []);

  const fetchBillingStatus = useCallback(async () => {
    try {
      const response = await api.get('/organizations/current');
      const orgData = response.data;
      setBillingStatus(orgData);
      const locked = orgData.is_locked_for_billing && !orgData.admin_billing_override;
      setIsBillingLocked(locked);
      billingLockedRef.current = locked;
      return orgData;
    } catch (error) {
      // Don't log 401s (user not authenticated yet)
      if (error.response?.status !== 401) {
        console.error('Failed to fetch billing status:', error);
      }
      return null;
    }
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await api.get('/users/me');
      setUser(response.data);

      // Phase 2: Default facility context by role.
      // ADMIN/MANAGER default to "all" (Admin = org-wide, Manager = all
      // assigned facilities enforced server-side). OPERATOR/TECHNICIAN
      // lock to their single assigned facility. `currentFacility` may
      // already be set from a prior tab/session — respect that.
      if (!currentFacility) {
        const role = response.data.role;
        if (role === 'ADMIN' || role === 'MANAGER') {
          setCurrentFacility('all');
        } else if (response.data.facility_ids?.length > 0) {
          setCurrentFacility(response.data.facility_ids[0]);
        }
      }

      // Fetch billing status after user is loaded
      await fetchBillingStatus();
    } catch (error) {
      // 401 means not authenticated — just clear state silently
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [fetchBillingStatus, currentFacility]);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const login = async (email, password, rememberMe = false) => {
    const response = await api.post('/auth/login', {
      email,
      password,
      remember_me: rememberMe
    });

    // Cookies are set automatically by the browser from Set-Cookie headers
    const { user: userData } = response.data;
    setUser(userData);

    // Phase 2: role-based default facility on login (see fetchCurrentUser).
    if (userData.role === 'ADMIN' || userData.role === 'MANAGER') {
      setCurrentFacility('all');
    } else if (userData.assignedFacilities?.length > 0) {
      setCurrentFacility(userData.assignedFacilities[0]);
    }

    return userData;
  };

  const signup = async (data) => {
    const response = await api.post('/auth/signup', data);
    return response.data;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (_) {
      // Best-effort; clear local state regardless
    }
    setUser(null);
    setCurrentFacility(null);
    setBillingStatus(null);
    setIsBillingLocked(false);
    billingLockedRef.current = false;
  };

  const forgotPassword = async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  };

  const resetPassword = async (tokenValue, password, confirmPassword) => {
    const response = await api.post('/auth/reset-password', {
      token: tokenValue,
      password,
      confirm_password: confirmPassword
    });
    return response.data;
  };

  const verifyEmail = async (tokenValue) => {
    const response = await api.post('/auth/verify-email', { token: tokenValue });
    return response.data;
  };

  const acceptInvitation = async (tokenValue, password, confirmPassword) => {
    const response = await api.post('/auth/accept-invitation', {
      token: tokenValue,
      password,
      confirm_password: confirmPassword
    });
    return response.data;
  };

  const hasRole = (roles) => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  const value = {
    user,
    loading,
    currentFacility,
    setCurrentFacility,
    login,
    signup,
    logout,
    forgotPassword,
    resetPassword,
    verifyEmail,
    acceptInvitation,
    hasRole,
    api,
    refreshUser: fetchCurrentUser,
    // Billing exports
    billingStatus,
    isBillingLocked,
    fetchBillingStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
